import { type JSX, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { pushSupportCaseGAEventToDataLayer } from '../../../common/analytics.js';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { categoryMsg, eInvoiceMsg, t } from '../../../common/i18n/index.js';
import { Dropdown } from '../../../common/react-hook-form/components/Dropdown.js';
import { Message, TextInput } from '../../../common/react-hook-form/fields/index.js';
import { getDefaultElectronicOperator } from '../../../common/utils/billingAccountUtils';
import { SupportCase } from '../../../generated/api/models.js';
import { useAuth } from '../../../public/site/AuthProvider.js';
import { EOperatorChangeRequestForm } from '../EOperatorChange/EOperatorChangeRequestForm.js';
import { getFeatureTranslations } from '../contactSupportFormUtils';
import type { BillChannel } from '../../../generated/api/models.js';
import type { EOperatorChangeRequestFormValues } from '../EOperatorChange/EOperatorChangeRequestForm.js';
import type { ContactSupportFormValues, ContactSupportInfo } from '../index.js';

import './SupportCaseContent.scss';

interface ExtraFieldProps {
  placeholder: string;
  label: string;
}

export interface SupportCaseContentProps {
  error?: string;
  extraField?: ExtraFieldProps;
  extraInfo?: JSX.Element;
  selfService: boolean;
  isEditing: boolean;
  isSubmitting?: boolean;
  onEOperatorChangeRequestFormSubmit: (
    formValues: EOperatorChangeRequestFormValues,
    contactValues: ContactSupportInfo
  ) => void;
  onSetEditMode?: (isEditing: boolean) => void;
  billChannels: BillChannel[];
}

export const SupportCaseContent = ({
  extraInfo,
  extraField,
  selfService,
  isEditing,
  isSubmitting,
  onEOperatorChangeRequestFormSubmit,
  onSetEditMode = () => {},
  billChannels,
}: SupportCaseContentProps) => {
  const { getValues, reset } = useFormContext<ContactSupportFormValues & EOperatorChangeRequestFormValues>();
  const values = getValues();
  const { authenticatedUser } = useAuth();
  const [isEOperatorChange, setIsEOperatorChange] = useState(false);
  const defaultOperator = getDefaultElectronicOperator(billChannels);
  const defaultElectronicAddress =
    defaultOperator?.active && defaultOperator?.electronicAddress ? defaultOperator.electronicAddress : '';
  const features = Object.entries(getFeatureTranslations()).map(([value, label]) => ({ label, value }));

  const onFeatureSelected = (feature: string) => {
    const eOperatorChangeSelected = feature === SupportCase.FeatureEnum.BILLING_E_OPERATOR_CHANGES;
    setIsEOperatorChange(eOperatorChangeSelected);
    if (eOperatorChangeSelected) {
      reset({
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        businessId: values.businessId,
        businessName: values.businessName,
        accountMasterId: values.accountMasterId || values.masterId,
        attachments: [],
        feature: SupportCase.FeatureEnum.BILLING_E_OPERATOR_CHANGES,
        willChangeTakeEffectImmediately: true,
        actingOnBehalfOfCompany: false,
        allBillingAccountsApply: true,
        deliveryMethod: t.IK1D(eInvoiceMsg),
        billLanguage: authenticatedUser?.preferredLanguage?.toUpperCase(),
        isBillingContactPerson: true,
        payerNameOrAddressChange: false,
        eOperator: defaultOperator?.id,
        eInvoiceAddress: defaultElectronicAddress,
      });
    } else {
      onSetEditMode(true);
    }
    pushSupportCaseGAEventToDataLayer('form_category_select', selfService, feature);
  };

  return (
    <div className={`of-support-case-description ${dsClass.MARGIN_TOP_6}`}>
      <h4>{t.TPI4('Your message')}</h4>
      <Dropdown label={t.CV5H(categoryMsg)} name="feature" items={features} onValueChange={onFeatureSelected} />
      {extraInfo}
      {extraField && (
        <div>
          <TextInput name="extraField" label={extraField.label} placeholder={extraField.placeholder} required={false} />
        </div>
      )}
      {isEOperatorChange ? (
        <EOperatorChangeRequestForm
          isAnonymousUser={!selfService}
          isEditing={isEditing}
          isSubmitting={isSubmitting}
          contactValues={values}
          onSubmit={onEOperatorChangeRequestFormSubmit}
          onSetEditMode={onSetEditMode}
          billChannels={billChannels}
        />
      ) : (
        <div className={dsClass.MARGIN_TOP_1}>
          <Message name="description" placeholder={t.TQLE('Write a message')} required={true} />
        </div>
      )}
    </div>
  );
};
